import { ShellElement } from './../../../common/shell-element'
import type { GlobalSearchResultPreview } from './../global-search-models'
import { html, property } from 'lit-element'
import searchResult from './search-result.styles.scss'
import { SVG_SEARCH_OUTLINED } from '@getgo/chameleon-icons'
import { asTranslationKey } from '../../../common/translate-helpers/i18n-utils'
import { repeat } from 'lit-html/directives/repeat'
import { NUMBER_OF_PREVIEW_ITEMS } from './samples'
import { t } from '../../../directives/translate'
import { getGlobalSearchManager } from '../global-search-manager'
import { nothing } from 'lit-html'

export const VIEW_MORE_RESULTS_BUTTON_CLICK = 'viewmoreresultsclick'
export interface SearchResultProps {
  readonly searchResults: readonly GlobalSearchResultPreview[]
}
export class SearchResult extends ShellElement implements SearchResultProps {
  static readonly tagName = 'goto-search-result'

  @property({ type: String }) searchString = ''
  @property({ type: Array }) searchResults: SearchResultProps['searchResults'] = []

  private noResultFoundSearch = asTranslationKey('No results found')

  static get styles() {
    return searchResult
  }

  private renderNoResultsActions() {
    const actions = getGlobalSearchManager().getActionsForSearchString(this.searchString, 'preview')
    return html`
      ${actions?.length
        ? html`<goto-global-search-actions
            .actions=${actions}
            searchString=${this.searchString}
          ></goto-global-search-actions> `
        : nothing}
    `
  }

  private renderNoResultsFoundElement() {
    return html`
      <div class="search-result-not-found search-result-not-found--header">
        <chameleon-typography color="type-color-disabled">${this.noResultFoundSearch}</chameleon-typography>
      </div>
      <div class="search-result-not-found  search-result-not-found--body">
        <chameleon-svg slot="start" class="right">${SVG_SEARCH_OUTLINED}</chameleon-svg>
        <chameleon-typography class="center"> ${this.searchString} </chameleon-typography>
        ${this.renderNoResultsActions()}
      </div>
    `
  }

  private searchResultsPreviewElement() {
    return this.searchResults.length <= NUMBER_OF_PREVIEW_ITEMS + 1
      ? this.renderSearchResultsPreviewList(this.searchResults)
      : this.renderSearchResultsPreviewListWithMore()
  }

  private renderSearchResultsPreviewList(searchResults: readonly GlobalSearchResultPreview[]) {
    return html`${repeat(
      searchResults,
      result => result.title,
      result => this.renderSearchResultPreviewItem(result),
    )}`
  }

  private renderSearchResultsPreviewListWithMore() {
    const shortenedPreviewList = this.searchResults.slice(0, NUMBER_OF_PREVIEW_ITEMS)
    return html` ${this.renderSearchResultsPreviewList(shortenedPreviewList)}
      <chameleon-button
        class="more-results-button"
        fullwidth
        variant="tertiary"
        size="small"
        @click="${() => {
          this.dispatchEvent(new CustomEvent(VIEW_MORE_RESULTS_BUTTON_CLICK))
        }}"
        >${t('View more results')} (${this.searchResults.length - NUMBER_OF_PREVIEW_ITEMS})</chameleon-button
      >`
  }

  private renderSearchResultPreviewItem(result: any) {
    return html` <goto-search-result-preview-item
      .searchResult=${result}
      .searchString=${this.searchString}
      tabindex="0"
    ></goto-search-result-preview-item>`
  }

  render() {
    return html`
      ${this.searchResults.length ? this.searchResultsPreviewElement() : this.renderNoResultsFoundElement()}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    readonly [SearchResult.tagName]: SearchResult
  }
}
